import { target } from '@github/catalyst';
import { dispatchChange } from '../util';
import {
  AnnotationEditorUIManager,
  PDFViewer,
  PDFViewerApplication
} from './pdfjs';

export abstract class PDFViewerBase extends HTMLElement {
  @target declare iframe: HTMLIFrameElement;
  pdfLoaded = Promise.withResolvers<PDFViewerApplication>();
  viewerApplication: PDFViewerApplication | undefined;
  annotationEditorUiManager: AnnotationEditorUIManager | undefined;
  detachedController: AbortController | undefined;

  abstract onAnnotationFieldClick?(
    viewerApplication: PDFViewerApplication,
    target: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement,
    event: Event
  ): void;

  abstract onAnnotationFieldFocus?(
    viewerApplication: PDFViewerApplication,
    target: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement,
    event: Event
  ): void;

  abstract onAnnotationFieldChange?(
    viewerApplication: PDFViewerApplication,
    target: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement,
    event: Event
  ): void;

  abstract onCtrlS?(viewerApplication: PDFViewerApplication): void;

  abstract onAnnotationLayerRendered?(
    viewerApplication: PDFViewerApplication
  ): void;

  abstract onAnnotationEditorUiManager?(args: {
    source: PDFViewer;
    uiManager: AnnotationEditorUIManager;
  }): void;

  abstract onPdfLoaded?(viewerApplication: PDFViewerApplication): void;

  connectedCallback() {
    this.detachedController = new AbortController();
  }

  disconnectedCallback() {
    this.detachedController?.abort();
  }

  async iframeLoaded() {
    const iframe = this.iframe;
    const viewerWindow = iframe.contentWindow;
    if (!viewerWindow) {
      throw new Error('Expected iframe contentWindow');
    }

    const viewerApplication = (this.viewerApplication = (viewerWindow as any)
      .PDFViewerApplication as PDFViewerApplication);

    viewerApplication.eventBus.on(
      'annotationeditoruimanager',
      args => {
        this.annotationEditorUiManager = args.uiManager;
        this.onAnnotationEditorUiManager?.(args);
      },
      {
        signal: this.detachedController?.signal
      }
    );

    await this.viewerApplication.initializedPromise;

    // hide the right toolbar containing the editor mode, print, download, and page scroll mode buttons.
    this.iframe.contentWindow!.document.getElementById(
      'toolbarViewerRight'
    )!.hidden = true;

    if (!this.hasAttribute('restore-position')) {
      // Prevent remembering the last page viewed.
      viewerApplication.preferences.set('viewOnLoad', 1); // 1 = initial position
      viewerApplication.preferences.set('defaultZoomValue', 'auto');
      viewerApplication.preferences.set('sidebarViewOnLoad', 0); // 0 = none, 1 = thumbnails, 2 = outline, 3 = attachments
      viewerApplication.preferences.set('scrollModeOnLoad', 0); // 0 = vertical scrolling
      viewerApplication.preferences.set('spreadModeOnLoad', 0); // 0 = no spreads
    }
    viewerApplication.preferences.set('enableSignatureEditor', true);

    // Prevent drop from updating the PDF document.
    viewerApplication.appConfig.mainContainer.addEventListener(
      'drop',
      event => {
        event.preventDefault();
        event.stopPropagation();
        console.log('STOPPED DROP');
      },
      { capture: true, signal: this.detachedController?.signal }
    );

    // Remove menu item which opens another PDF file.
    // TODO: remove the whole right menu? (secondaryToolbar.toolbar).
    const openFileMenuItem =
      viewerApplication.appConfig.secondaryToolbar.openFileButton;
    if (
      openFileMenuItem?.nextElementSibling?.matches(
        '.horizontalToolbarSeparator'
      )
    ) {
      openFileMenuItem.nextElementSibling.toggleAttribute('hidden', true);
    }
    openFileMenuItem?.toggleAttribute('hidden', true);

    // Field events
    viewerApplication.appConfig.viewerContainer.addEventListener(
      'click',
      this.handleAnnotationClick,
      { signal: this.detachedController?.signal }
    );
    viewerApplication.appConfig.viewerContainer.addEventListener(
      'focus',
      this.handleAnnotationFocus,
      {
        capture: true,
        signal: this.detachedController?.signal
      }
    );
    viewerApplication.appConfig.viewerContainer.addEventListener(
      'change',
      this.handleAnnotationChange,
      {
        signal: this.detachedController?.signal
      }
    );

    this.iframe.contentWindow?.window.addEventListener(
      'keydown',
      this.handleKeydown,
      {
        capture: true,
        signal: this.detachedController?.signal
      }
    );

    viewerApplication.eventBus.on(
      'annotationlayerrendered',
      () => this.onAnnotationLayerRendered?.(viewerApplication),
      {
        signal: this.detachedController?.signal
      }
    );

    await Promise.all([
      new Promise<void>(resolve =>
        viewerApplication.eventBus.on('documentloaded', resolve, {
          once: true,
          signal: this.detachedController?.signal
        })
      )
    ]);

    // Prevent edits to PDF from interrupting beforeunload event.
    viewerApplication.pdfDocument.annotationStorage.onSetModified = null;
    viewerApplication.pdfDocument.annotationStorage.onResetModified = null;
    viewerApplication.pdfDocument.annotationStorage.onAnnotationEditor = null;

    this.pdfLoaded.resolve(viewerApplication);
    this.onPdfLoaded?.(viewerApplication);

    // https://chatgpt.com/share/683136c5-49d4-8000-ac27-926e2e29d12e
    // @ts-ignore
    // console.log(this.iframe.contentWindow.pdfjsLib);
    // @ts-ignore
    // console.log(this.iframe.contentWindow.pdfjsViewer);
  }

  isAnnotationField(
    target: EventTarget | null
  ): target is HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement {
    return (
      target &&
      'matches' in target &&
      typeof target.matches === 'function' &&
      target.matches(
        'section[data-annotation-id] [data-element-id]:is(input, select, textarea)'
      )
    );
  }

  handleAnnotationClick = async (event: Event) => {
    const target = event.target;
    if (!this.isAnnotationField(target) || !this.viewerApplication) {
      return;
    }

    // Make radios uncheckable.
    if (
      target.type === 'radio' &&
      target instanceof this.iframe.contentWindow!.window.HTMLInputElement &&
      target.checked
    ) {
      // Click happens before the change event.
      // Wait for the change event to fire, if it does, we do not need to clear the radio because it just became checked.
      const timeout = setTimeout(() => {
        target.checked = false;
        dispatchChange(target);
      });
      target.addEventListener('change', () => clearTimeout(timeout), {
        once: true,
        capture: true
      });
    }

    this.onAnnotationFieldClick?.(this.viewerApplication, target, event);
  };

  handleAnnotationFocus = async (event: Event) => {
    const target = event.target;
    if (!this.isAnnotationField(target) || !this.viewerApplication) {
      return;
    }
    this.onAnnotationFieldFocus?.(this.viewerApplication, target, event);
  };

  handleAnnotationChange = (event: Event) => {
    const target = event.target;
    if (!this.isAnnotationField(target) || !this.viewerApplication) {
      return;
    }
    this.onAnnotationFieldChange?.(this.viewerApplication, target, event);
  };

  handleKeydown = (event: KeyboardEvent) => {
    if (
      !(event.ctrlKey || event.metaKey) ||
      event.shiftKey ||
      event.altKey ||
      event.key !== 's' ||
      event.defaultPrevented ||
      !this.viewerApplication
    ) {
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation(); // Prevents the default save dialog from opening.
    const target = event.target;
    if (this.isAnnotationField(target)) {
      target.blur();
      target.focus();
    }
    this.onCtrlS?.(this.viewerApplication);
  };
}
